import {Fragment} from "react";
import {IconButton, Tooltip} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useBoolState} from "@/hooks";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import ResyncProgressDialog from "../../../ResyncProgressDialog";
import {FormattedMessage, defineMessages} from "react-intl";
import {ProvisionedDevice} from "../../types";

type Props = {
    device: ProvisionedDevice;
};

// Static message descriptors for the possible disabled reasons
const RESYNC_DISABLED_REASON_MESSAGES = defineMessages({
    call_control_disabled: {
        id: "devices.provisioned.resync.disabled_reasons.call_control_disabled",
        defaultMessage: "the call-control is disabled",
    },
    no_mac_address: {
        id: "devices.provisioned.resync.disabled_reasons.no_mac_address",
        defaultMessage: "the MAC address is missing",
    },
});

type DisabledReason = keyof typeof RESYNC_DISABLED_REASON_MESSAGES;

const ResyncButton = ({device: {id, macAddress}}: Props) => {
    const structConfig = useStructureConfig();
    const [isOpen, open, close] = useBoolState();

    let disabledReason: DisabledReason | null = null;
    if (!structConfig.isEnabledService("callControl")) disabledReason = "call_control_disabled";
    else if (!macAddress) disabledReason = "no_mac_address";

    const enabled = disabledReason === null;

    return (
        <Fragment>
            <Tooltip
                title={
                    <span>
                        <FormattedMessage
                            id="devices.provisioned.resync.disabled_reasons.title"
                            defaultMessage="Resynchronize is disabled because"
                        />{" "}
                        <FormattedMessage {...RESYNC_DISABLED_REASON_MESSAGES[disabledReason]} />
                    </span>
                }
                arrow
                disableHoverListener={enabled}
            >
                <span>
                    <IconButton color="primary" size="small" onClick={open} disabled={!enabled}>
                        <FontAwesomeIcon icon="sync-alt" />
                    </IconButton>
                </span>
            </Tooltip>
            <ResyncProgressDialog deviceIdList={[id]} isOpen={isOpen} close={close} name={macAddress} />
        </Fragment>
    );
};

export default ResyncButton;
